import { useState } from "react";
import { motion } from "framer-motion";
import Button from "../ui/Button";
import DiceRoller from "./DiceRoller";
import { useLanguage } from "../../i18n/LanguageContext";
import type { Player, RiskLevel } from "../../types/game";
import { useGameStore } from "../../store/gameStore";

interface Props { player: Player; }

export default function InvestmentPanel({ player }: Props) {
  const { dispatch, lastDiceResult, pendingInvestment } = useGameStore();
  const { t } = useLanguage();
  const [riskLevel, setRiskLevel] = useState<RiskLevel>("low");
  const [amount, setAmount] = useState(20);
  const [mode, setMode] = useState<"choose" | "rolling" | "portfolio">("choose");
  const [committedRisk, setCommittedRisk] = useState<RiskLevel>("low");

  const canAfford = player.cash >= amount;

  const handleCommit = () => {
    if (!canAfford) return;
    setCommittedRisk(riskLevel);
    dispatch({ type: "COMMIT_INVEST", riskLevel, amount });
    if (riskLevel === "bond") {
      // Bonds skip dice — go straight to next phase
      dispatch({ type: "SKIP_INVEST" });
    } else {
      setMode("rolling");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        <button
          className={`p-2 rounded-xl border transition-all ${mode === "choose" ? "border-earnings bg-earnings/20 text-earnings" : "border-white/10 text-white/50 hover:border-white/30"}`}
          onClick={() => setMode("choose")}
        >
          {t.action.invest}
        </button>
        <button
          className={`p-2 rounded-xl border transition-all ${mode === "portfolio" ? "border-earnings bg-earnings/20 text-earnings" : "border-white/10 text-white/50 hover:border-white/30"}`}
          onClick={() => setMode("portfolio")}
        >
          {t.action.portfolio} ({player.investments.length})
        </button>
        <button
          className="p-2 rounded-xl border border-white/10 text-white/50 hover:border-white/30 transition-all"
          onClick={() => dispatch({ type: "SKIP_INVEST" })}
        >
          {t.action.skip}
        </button>
      </div>

      {mode === "choose" && (
        <motion.div className="space-y-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="grid grid-cols-3 gap-2">
            {(["bond", "low", "high"] as RiskLevel[]).map((r) => (
              <button
                key={r}
                onClick={() => setRiskLevel(r)}
                className={`p-3 rounded-xl border text-left transition-all ${riskLevel === r ? "border-earnings bg-earnings/20" : "border-white/10 hover:border-white/30"}`}
              >
                <div className="font-bold text-white text-xs">
                  {r === "bond" ? t.action.bondLabel : r === "low" ? t.action.lowRisk : t.action.highRisk}
                </div>
                <div className="text-xs text-white/50 mt-1">
                  {r === "bond" ? t.action.bondDesc : r === "low" ? t.action.lowRiskDesc : t.action.highRiskDesc}
                </div>
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm text-white/60">
              <span>{t.action.amount}</span>
              <div className="text-right">
                <span className="text-gold font-bold">🪙{amount}</span>
                <span className="text-white/30 text-xs ml-2">
                  → +{Math.max(1, Math.floor(amount * (riskLevel === "bond" ? 0.04 : riskLevel === "low" ? 0.05 : 0.10)))}{t.action.perRound}
                </span>
              </div>
            </div>
            <input
              type="range" min={20} max={Math.min(player.cash, 200)} step={10} value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full accent-blue-500"
            />
            <div className="flex justify-between text-xs text-white/30">
              <span>20</span>
              <span>{t.action.available(player.cash)}</span>
            </div>
          </div>

          <Button onClick={handleCommit} disabled={!canAfford || amount < 20} size="lg" className="w-full">
            {t.action.investBtn(amount)}
          </Button>
        </motion.div>
      )}

      {mode === "rolling" && (
        <motion.div className="flex flex-col items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="text-white/60 text-sm">
            {t.action.investingIn} 🪙{pendingInvestment?.amount ?? amount} — {t.action.inFund(committedRisk)}
          </div>
          <DiceRoller onRoll={() => dispatch({ type: "ROLL_DICE" })} result={lastDiceResult} />
          {lastDiceResult && (
            <Button onClick={() => dispatch({ type: "SKIP_INVEST" })} size="lg" className="w-full">{t.action.continueTo}</Button>
          )}
        </motion.div>
      )}

      {mode === "portfolio" && (
        <motion.div className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {player.investments.length === 0 ? (
            <div className="text-center text-white/40 py-4">{t.action.noInvestments}</div>
          ) : (
            player.investments.map((inv) => (
              <div key={inv.id} className="bg-white/5 rounded-xl p-3 border border-white/10 flex justify-between items-center">
                <div>
                  <div className="font-bold text-white text-sm">{inv.name}</div>
                  <div className="text-xs text-white/50">
                    {inv.riskLevel === "bond"
                      ? <span>{t.action.bondPortfolio(inv.passiveIncomePerRound)}</span>
                      : <>{t.action.paidNowRound(inv.purchasePrice, inv.currentValue, inv.passiveIncomePerRound)}{inv.passiveIncomePerRound === 0 && <span className="text-orange-400/60 ml-1">(invest more to earn passive income)</span>}</>
                    }
                  </div>
                </div>
                <button
                  onClick={() => dispatch({ type: "SELL_INVESTMENT", investmentId: inv.id })}
                  className="text-xs text-red-400 hover:text-red-300 font-bold px-2 py-1 rounded-lg hover:bg-red-500/10 transition-all"
                >
                  {t.action.sell}
                </button>
              </div>
            ))
          )}
          <Button variant="secondary" onClick={() => dispatch({ type: "SKIP_INVEST" })} className="w-full">
            {t.action.doneInvestments}
          </Button>
        </motion.div>
      )}
    </div>
  );
}
